import { useEffect, useRef, useState } from "react";
import { Mic } from "lucide-react";
import { cn } from "@/cn";

export function AssistantComposer({
  placeholder,
  disabled,
  onSend,
}: {
  placeholder: string;
  disabled?: boolean;
  onSend: (message: string) => void;
}) {
  const [value, setValue] = useState("");
  const [listening, setListening] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [value]);

  function submit() {
    const msg = value.trim();
    if (!msg || disabled) return;
    onSend(msg);
    setValue("");
  }

  function dictate() {
    type SpeechRec = {
      lang: string;
      interimResults: boolean;
      onstart: (() => void) | null;
      onend: (() => void) | null;
      onresult: ((ev: {
        results: ArrayLike<ArrayLike<{ transcript: string }>>;
      }) => void) | null;
      start: () => void;
    };
    const w = window as unknown as {
      SpeechRecognition?: new () => SpeechRec;
      webkitSpeechRecognition?: new () => SpeechRec;
    };
    const SR = w.SpeechRecognition || w.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.onstart = () => setListening(true);
    rec.onend = () => setListening(false);
    rec.onresult = (ev) => {
      let transcript = "";
      for (let i = 0; i < ev.results.length; i++) {
        transcript += ev.results[i][0].transcript;
      }
      setValue(transcript);
    };
    rec.start();
  }

  return (
    <div className="relative w-full max-w-2xl">
      <div className="rounded-xl border border-border bg-card p-3 shadow-sm">
        <div className="flex items-end gap-2">
          <textarea
            ref={taRef}
            rows={1}
            value={value}
            disabled={disabled}
            placeholder={placeholder}
            aria-label={placeholder}
            className="max-h-40 min-h-[40px] flex-1 resize-none bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
          />
          <button
            type="button"
            title="Dictate"
            aria-label="Dictate"
            className={cn(
              "shrink-0 rounded-md p-2 text-muted-foreground hover:bg-muted/30 hover:text-foreground",
              listening && "text-brand",
            )}
            onClick={dictate}
          >
            <Mic className="size-4" />
          </button>
        </div>
        <div className="mt-2 flex items-center justify-end">
          <button
            type="button"
            disabled={disabled || !value.trim()}
            className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground disabled:opacity-50"
            onClick={submit}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
